import { FunctionComponent, useState } from 'react';
import Box from '@material-ui/core/Box';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { BodySection, ActionSection } from 'components/UI/Section';
import Section from '../Section';
import ServiceName from './ServiceName';
import ServiceDuration from './ServiceDuration';
import Service, { ServiceDuration as Duration, isServiceDuration } from 'model/Service';
import ColorManager from './ColorManager';
import { useMasterData, useMarketplace, useAuth } from 'hooks';
import ServiceDescription from './ServiceDescription';
import ServiceReport from './ServiceReport';
import BusinessLocation from './BusinessLocation';
import ServiceOnlineBooking from './ServiceOnlineBooking';
import ServiceFormsSection from 'components/Service/Forms/ServiceFormsSection';
import { AssociatedForm } from 'model/Forms';
import { MobileDivider } from 'components/UI/MobileDivider';
import { Option, OWNER_ID } from '@spike/model';
import { debugConsoleLog } from 'utils/GeneralUtils';
import { FieldErrorWithKey } from '../model';
import { getSimpleDuration, getVariableDuration } from 'utils/ServiceUtils';

interface Props {
    service: Service;
    collapsed?: boolean;
    disabled?: boolean;
    editable?: boolean;
    completed?: boolean;
    modifiable?: boolean;
    isAdmin: boolean;
    errors: Array<FieldErrorWithKey>;
    onEdit?: () => void;
    onNext: () => void;
    onNameChange: (name: string) => void;
    onDescriptionChange: (description: string) => void;
    onDurationChange: (duration: Duration) => void;
    onColorChange: (color: string | null) => void;
    onReportChange: (report: boolean) => void;
    onBusinessLocationChange?: (businessLocation: any) => void;
    onOnlineBookingChange?: (onlineBooking: boolean) => void;
    onFormsChange?: (forms: AssociatedForm[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        nameDescriptionDurationContainer: {
            display: 'flex',
            flexDirection: 'column',

            [theme.breakpoints.up('md')]: {
                width: '50%',
                paddingRight: '0px'
            },
            [theme.breakpoints.up('xl')]: {
                paddingRight: '0px'
            }
        },
        nameDescriptionDurantionColorContainer: {
            [theme.breakpoints.up('md')]: {
                display: 'flex',
                flexDirection: 'row'
            }
        },
        nameContainer: {
            display: 'flex',
            width: '100%',
            flexDirection: 'column'
        },
        descriptionContainer: {
            marginTop: 14,

            [theme.breakpoints.up('md')]: {
                marginTop: 31
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: 42
            }
        },
        durationContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',

            [theme.breakpoints.up('md')]: {
                display: 'grid',
                gridTemplateColumns: '50% 50%',
                marginTop: '44px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '42px'
            }
        },
        durationColumn: {
            [theme.breakpoints.up('md')]: {
                gridColumn: 1,
                gridRow: 1
            }
        },
        formsColumn: {
            flexShrink: 0,
            overflow: 'visible',
            minWidth: 0,
            width: '100%',

            [theme.breakpoints.up('md')]: {
                gridColumn: 1,
                gridRow: 2
            }
        },
        formsSection: {
            [theme.breakpoints.down('sm')]: {
                marginTop: '0 !important'
            }
        },
        formsDivider: {
            margin: '0px -16px',

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        reportColumn: {
            [theme.breakpoints.up('md')]: {
                gridColumn: 2,
                gridRow: 1
            }
        },
        businessContainer: {
            width: '100%',

            [theme.breakpoints.up('md')]: {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginTop: '44px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '42px'
            }
        },
        colorReportContainer: {
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            },
            [theme.breakpoints.up('md')]: {
                width: '50%',
                paddingLeft: '50px'
            },
            [theme.breakpoints.up('xl')]: {
                paddingLeft: '50px'
            }
        }
    })
);

const emptyHours = 0;
const emptyMinutes = 0;

export const Details: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const marketplace = useMarketplace();
    const auth = useAuth();

    const isOwner = auth.user!.role.id === OWNER_ID;

    // Type assertion - we expect ServiceDuration for grooming services
    const getGroomingDuration = (service: Service): Duration => {
        if (isServiceDuration(service.duration)) {
            return service.duration;
        }

        // Return a safe default to prevent crashes
        return {
            simple: { hours: 1, minutes: 0 },
            variable: []
        };
    };

    const isDurationComplete = (service: Service): boolean => {
        const simple = getSimpleDuration(service);
        const variable = getVariableDuration(service);

        return (
            (simple !== undefined && (simple.hours !== emptyHours || simple.minutes !== emptyMinutes)) ||
            (variable.length > 0 &&
                variable.filter(
                    variableDuration =>
                        variableDuration.duration.hours === emptyHours &&
                        variableDuration.duration.minutes === emptyMinutes
                ).length === 0)
        );
    };

    const isComplete = (name: string, service: Service): boolean => {
        return name.length > 0 && isDurationComplete(service);
    };

    const [disabledNext, setDisabledNext] = useState(!isComplete(props.service.name, props.service));

    const nameChangeHandler = (name: string) => {
        props.onNameChange(name);
        setDisabledNext(!isComplete(name, props.service));
    };

    const descriptionChangeHandler = (description: string) => {
        props.onDescriptionChange(description);
    };

    const durationChangeHandler = (duration: Duration) => {
        props.onDurationChange(duration);
        // Create a temporary service object to check completion
        const tempService = { ...props.service, duration };
        setDisabledNext(!isComplete(props.service.name, tempService));
    };

    const colorsChangeHandler = (color: string | null) => {
        props.onColorChange(color);
    };

    const businessLocationChange = (isMobile: boolean) => {
        props.onBusinessLocationChange && props.onBusinessLocationChange(isMobile);
    };

    const onlineBookingChange = (active: boolean) => {
        debugConsoleLog(String(active));
        props.onOnlineBookingChange && props.onOnlineBookingChange(active);
    };

    const reportChangeHandler = (report: boolean) => {
        props.onReportChange(report);
    };

    const nextHandler = () => {
        props.onNext();
    };

    return (
        <Section
            position={2}
            title="Services Details"
            {...props}
        >
            <BodySection>
                <Box className={classes.container}>
                    <Box className={classes.nameDescriptionDurantionColorContainer}>
                        <Box className={classes.nameDescriptionDurationContainer}>
                            <Box className={classes.nameContainer}>
                                <ServiceName
                                    text={props.service.name || ''}
                                    error={props.errors.find(error => error.fieldName === 'name')}
                                    onChange={nameChangeHandler}
                                />
                            </Box>
                            <Box className={classes.descriptionContainer}>
                                <ServiceDescription
                                    text={props.service.description}
                                    onChange={descriptionChangeHandler}
                                    max={400}
                                />
                            </Box>
                        </Box>
                        <Box className={classes.colorReportContainer}>
                            <ColorManager
                                color={props.service.color}
                                onChange={colorsChangeHandler}
                            />
                        </Box>
                    </Box>
                    <Box className={classes.businessContainer}>
                        <BusinessLocation
                            settings={marketplace.settings}
                            editable={props.editable}
                            houseCall={props.service.houseCall}
                            onChange={businessLocationChange}
                        />
                        {(props.isAdmin || isOwner) && (
                            <ServiceOnlineBooking
                                editable={props.editable}
                                active={props.service.olbEnabled}
                                onChange={onlineBookingChange}
                            />
                        )}
                    </Box>
                    <Box className={classes.durationContainer}>
                        <Box className={classes.durationColumn}>
                            <ServiceDuration
                                petType={props.service.petTypes}
                                duration={getGroomingDuration(props.service)}
                                petSizes={
                                    marketplace.petTypes.dogs
                                        ? marketplace.petTypes.dogs?.sizes
                                              .map(e => e.element)
                                              .map(size => ({ ...size }))
                                        : []
                                }
                                onChange={durationChangeHandler}
                                errors={props.errors.filter(error => error.fieldName === 'duration')}
                            />
                        </Box>
                        <Box className={classes.formsColumn}>
                            <ServiceFormsSection
                                forms={props.service.forms || []}
                                serviceId={props.service.id}
                                serviceCategory="grooming"
                                disabled={props.disabled}
                                className={classes.formsSection}
                                onChange={forms => props.onFormsChange?.(forms)}
                            />
                            <Box className={classes.formsDivider}>
                                <MobileDivider />
                            </Box>
                        </Box>
                        <Box className={classes.reportColumn}>
                            <ServiceReport
                                report={props.service.hasReport}
                                onChange={reportChangeHandler}
                            />
                        </Box>
                    </Box>
                </Box>
            </BodySection>
            <ActionSection
                id="services_add_service_details_button_next"
                onNext={nextHandler}
                disabled={disabledNext}
            />
        </Section>
    );
};

export default Details;
